import { IListItem } from '../List';
import { GridCell } from './GridCell';
import { GridColumn } from './GridColumn';
import { GridRow } from './GridRow';
import { Grid, IGridColumnEditor } from './SimpleGridLight';
import { IGridPlugin } from '@Common/Controls/SimpleGridLight/Plugins/IGridPlugin';
import { CommonFilters } from './FilterManager';

export enum SortOrder {
    None = 0,
    Asc = 1,
    Desc = -1
}

export enum ColumnsFitType {
    None = 0,
    Fit = 1
}

export interface IValueChangeDelegate<T> {
    (newValue: T);
}

export interface IGridProperties<TData = any> {
    data?: any; // any[] | dataSource.IDataSource;
    columns?: IGridColumnProperties<TData>[];
    width?: string;
    height?: string;
    className?: string;
    css?: any;
    footer?: IGridFooter;
    rowBatchSize?: number;
    hideHeader?: boolean;
    pager?: IGridPager;
    validator?: (grid: Grid) => boolean;
    columnsFit?: ColumnsFitType;
    keyboardNav?: boolean | { newRowFactory?: () => void };
    plugins?: IGridPluginInitializer[];
    appearances?: IGridAppearances;
    behavior?: IGridBehavior;
}

export interface IGridPluginInitializer {
    (grid: Grid): IGridPlugin;
}

export enum SelectionMode {
    select,
    deselect
}

export interface IGridBehavior {
    selectionMode?: SelectionMode;
}

export interface IGridAppearances {
    cellStyleName?: string;
    headerCellStyleName?: string;
}

export interface IGridFooter {
    text?: string;
    content?: JQuery;
    css?: any;
    flexible?: boolean;
}

export interface IGridPager {
    pageSize?: number;
    startPage?: number;
}

export let defaultGridPagerSettting: IGridPager = {
    pageSize: undefined,
    startPage: 0
};

export class RenderArg<T = any> {
    constructor(public readonly _row: GridRow, public readonly _column: GridColumn, private isGetText: boolean = false) {}

    public row(): GridRow {
        return this._row;
    }

    public column(): GridColumn {
        return this._column;
    }

    public cell(): GridCell {
        return this._row.getCell(this._column);
    }

    public get data(): T {
        return this._row.getData();
    }

    public get value(): any {
        return this.cell().value();
    }

    public isText(): boolean {
        return this.isGetText;
    }
}

export type CellRender<TData = any> = (arg: RenderArg<TData>) => string | JQuery;

export interface IGridColumnProperties<TData = any, TKey = keyof TData> {
    dataIndex?: TKey;
    title?: string;
    width?: IColumnWidth;
    dataType?: string;
    isSortable?: boolean;
    sortOrder?: SortOrder;
    css?: any;
    cellCss?: any;
    className?: string;
    cellClassName?: string;
    render?: CellRender<TData>;
    getTitle?: CellRender;
    editor?: IGridColumnEditor;
    readonly?: boolean;
    compare?: (z, y) => number;
    header?: JQuery | string;
    dataAccessor?: {
        get?: (row: GridRow<TData>) => any;
        set?: (row: GridRow<TData>, value: any) => void;
    };
    setting?: IDynamicSetting<TData>;
    filter?: IFilterProperty;
    validator?: (cell: GridCell) => string[];
    validateByColumn?: boolean;
}

export interface IFilterOperation {
    // type filterOperation
    // filterProvider should be able to create Operation by it <operation>
    operation: CommonFilters;
    caption?: string;
    // predefined values for operation
    values?: any;
}

export enum FilterType {
    inPlace = 1,
    dropDown,
    popup,
    byValue
}

export interface IFilterProperty {
    filterType?: FilterType;
    /** type of column for create input
     filterProvider should be able to create control for it <type>*/
    type?: string;
    /**all supported column filter operations*/
    operations?: IFilterOperation[];
    values?: IListItem[];
    valuesIsArray?: boolean;
    defaults?: string[];
}

export interface IDynamicSetting<TData = any> {
    readonly?: keyof TData | ((cell: GridCell) => boolean);
    css?: string | ((cell: GridCell) => {});
}

export let classNames = {
    disable: 'disable',
    readonly: 'readonly',
    headerContainer: 'simpleGrid-headerContainer',
    bodyContainer: 'simpleGrid-bodyContainer',
    scrollableArea: 'simpleGrid-scrollableArea',
    headerTable: 'simpleGrid-header',
    bodyTable: 'simpleGrid-body',
    footer: 'simpleGrid-footer',
    column: 'simpleGrid-column',
    row: 'simpleGrid-row',
    selectedRow: 'simpleGrid-selectedRow',
    cell: 'simpleGrid-cell',
    sortableColumn: 'sortable',
    sortAsc: 'asc',
    sortDesc: 'desc'
};

export let defaultGridSetting: IGridProperties = {
    width: '100%',
    height: '100%',
    data: [],
    footer: {
        css: {},
        flexible: false
    },
    rowBatchSize: 100,
    hideHeader: false,
    columnsFit: ColumnsFitType.Fit,
    keyboardNav: false
} as IGridProperties;

export enum ColumnWidthType {
    none = 0,
    byContent = 1,
    byTitle = 2,
    byTitleContent = ColumnWidthType.byTitle | ColumnWidthType.byContent,
    star = 4
}

export interface IColumnWidth {
    desired?: number;
    min?: number;
    max?: number;
    type?: ColumnWidthType;
}

export class ColumnWidth implements IColumnWidth {
    constructor(width: IColumnWidth) {
        Object.assign(this, width);
    }

    public desired?: number;
    public min?: number = 0;
    public max?: number;
    public type: ColumnWidthType = ColumnWidthType.byTitleContent;
    public calcValue: number = 0;
    public userValue: number = 0;
}
